import React, { useState, useEffect } from 'react';
import { newsService } from '../../services/newsService';
import { storageService } from '../../services/storageService';
import { logsService } from '../../services/logsService';
import { webhooksService } from '../../services/webhooksService';
import { useAdminAuth } from '../../context/AdminAuthContext';
import ArticleBuilder from './ArticleBuilder';
import { formatDateEST } from '../../utils/timezoneUtils';
import '../../styles/NewsManager.css';

const NewsManager = () => {
  const { user, hasPermission, hasAnyPermission } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('list');
  const [builderTab, setBuilderTab] = useState('blocks');
  const [newsList, setNewsList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    authorName: '',
    scheduledDate: '',
    headerImage: null,
    headerImageUrl: ''
  });

  const [blocks, setBlocks] = useState([]);
  const [userArticles, setUserArticles] = useState([]);
  const [viewMode, setViewMode] = useState('my'); // 'my', 'review', 'all'
  const [previewArticle, setPreviewArticle] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishingNewsId, setPublishingNewsId] = useState(null);
  const [publisherNameInput, setPublisherNameInput] = useState('');
  const [mediaWebhooks, setMediaWebhooks] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadNews();
  }, [user, viewMode]);

  useEffect(() => {
    const loadMediaWebhooks = async () => {
      const result = await webhooksService.getWebhooks(true);
      if (result.success) {
        const mediaWebhooks = result.webhooks.filter(w => w.is_media_webhook);
        if (mediaWebhooks.length > 0) {
          setMediaWebhooks(mediaWebhooks);
        } else {
          setMediaWebhooks(result.webhooks);
        }
      }
    };
    loadMediaWebhooks();
  }, []);

  const loadNews = async () => {
    if (viewMode === 'my' && user?.id) {
      const result = await newsService.getUserNews(user.id, true);
      if (result.success) {
        setNewsList(result.news);
        setUserArticles(result.news);
      }
    } else if (viewMode === 'review') {
      const result = await newsService.getAllNews(true);
      if (result.success) {
        const filteredNews = result.news.filter(news => news.status === 'pending_review');
        setNewsList(filteredNews);
      }
    } else if (viewMode === 'all') {
      const result = await newsService.getAllNews(true);
      if (result.success) {
        setNewsList(result.news);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'file') {
      const file = e.target.files[0];
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({
          ...prev,
          [name]: file,
          headerImageUrl: previewUrl
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const getAuthorName = () => {
    if (user?.user_metadata?.name) return user.user_metadata.name;
    if (user?.email) return user.email.split('@')[0];
    return 'Unknown Author';
  };

  const handleCreateNews = async (e, saveAsDraft = false) => {
    e?.preventDefault?.();

    if (!formData.title.trim() || blocks.length === 0) {
      setMessage({ type: 'error', text: 'Title and at least one content block are required' });
      return;
    }

    setLoading(true);
    setUploadingImage(false);

    const authorName = formData.authorName || getAuthorName();
    let headerImageUrl = null;

    if (formData.headerImage) {
      setUploadingImage(true);
      const newsId = `draft-${Date.now()}`;
      const uploadResult = await storageService.uploadNewsHeaderImage(formData.headerImage, newsId);
      setUploadingImage(false);
      
      if (!uploadResult.success) {
        setMessage({ type: 'error', text: `Image upload failed: ${uploadResult.error}` });
        setLoading(false);
        return;
      }
      headerImageUrl = uploadResult.url;
    }

    const result = await newsService.createNews(
      formData.title,
      '',
      authorName,
      formData.scheduledDate || null,
      blocks,
      headerImageUrl,
      user?.id,
      saveAsDraft
    );

    if (result.success) {
      const action = saveAsDraft ? 'saved as draft' : 'submitted for review';
      setMessage({ type: 'success', text: `News article ${action} successfully` });
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'NEWS_CREATE',
          {
            title: formData.title,
            author: authorName,
            status: saveAsDraft ? 'draft' : 'pending_review',
            has_scheduled_date: !!formData.scheduledDate,
            block_count: blocks.length
          }
        );
      }
      
      setFormData({ title: '', content: '', authorName: '', scheduledDate: '', headerImage: null, headerImageUrl: '' });
      setBlocks([]);
      loadNews();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleUpdateNews = async (e, newsId, saveAsDraft = false) => {
    e?.preventDefault?.();

    if (!formData.title.trim() || blocks.length === 0) {
      setMessage({ type: 'error', text: 'Title and at least one content block are required' });
      return;
    }

    setLoading(true);
    setUploadingImage(false);

    const authorName = formData.authorName || getAuthorName();
    let headerImageUrl = formData.headerImageUrl || null;

    if (formData.headerImage && formData.headerImage instanceof File) {
      setUploadingImage(true);
      const uploadResult = await storageService.uploadNewsHeaderImage(formData.headerImage, newsId);
      setUploadingImage(false);
      
      if (!uploadResult.success) {
        setMessage({ type: 'error', text: `Image upload failed: ${uploadResult.error}` });
        setLoading(false);
        return;
      }
      headerImageUrl = uploadResult.url;
    }

    const updates = {
      title: formData.title,
      blocks: blocks,
      scheduled_date: null,
      header_image: headerImageUrl,
      status: saveAsDraft ? 'draft' : 'pending_review'
    };
    
    console.log('[NewsManager] Saving article with blocks:', JSON.stringify(blocks, null, 2));

    const result = await newsService.updateNews(
      newsId,
      updates,
      user?.id,
      authorName
    );

    if (result.success) {
      const action = saveAsDraft ? 'saved as draft' : 'submitted for review';
      setMessage({ type: 'success', text: `News article ${action} successfully` });
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'NEWS_UPDATE',
          {
            news_id: newsId,
            title: formData.title,
            save_as_draft: saveAsDraft,
            status: saveAsDraft ? 'draft' : 'pending_review',
            block_count: blocks.length
          }
        );
      }
      
      setEditingId(null);
      setFormData({ title: '', content: '', authorName: '', scheduledDate: '', headerImage: null, headerImageUrl: '' });
      setBlocks([]);
      loadNews();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleEditClick = (news) => {
    setEditingId(news.id);
    setFormData({
      title: news.title,
      content: news.content,
      authorName: news.author_name || '',
      scheduledDate: news.scheduled_date || '',
      headerImage: null,
      headerImageUrl: news.header_image || ''
    });
    const parsedBlocks = newsService.parseContent(news);
    setBlocks(Array.isArray(parsedBlocks) ? parsedBlocks : []);
    setActiveTab('create');
  };

  const handlePublish = async (newsId, isPublished) => {
    if (isPublished) {
      setLoading(true);
      const result = await newsService.unpublishNews(newsId);
      if (result.success) {
        setMessage({ type: 'success', text: 'Article unpublished successfully' });
        loadNews();
      } else {
        setMessage({ type: 'error', text: result.error });
      }
      setLoading(false);
    } else {
      setPublishingNewsId(newsId);
      setPublisherNameInput(getAuthorName());
      setPublishModalOpen(true);
    }
  };

  const handleConfirmPublish = async () => {
    if (!publisherNameInput.trim()) {
      setMessage({ type: 'error', text: 'Publisher name cannot be empty' });
      return;
    }

    setLoading(true);
    const result = await newsService.publishNews(publishingNewsId, user?.id, publisherNameInput);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Article published successfully' });
      
      if (selectedChannels.length > 0) {
        await webhooksService.triggerSpecificWebhooks(selectedChannels, 'news_published', {
          id: result.news.id,
          title: result.news.title,
          description: newsService.extractPlainText(result.news.content).substring(0, 200),
          author: result.news.author_name,
          url: `${window.location.origin}/news/${result.news.id}`,
          header_image: result.news.header_image,
          published_at: result.news.published_at
        });
      }
      
      setPublishModalOpen(false);
      setPublishingNewsId(null);
      setPublisherNameInput('');
      setSelectedChannels([]);
      loadNews();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    setLoading(true);

    const result = await newsService.deleteNews(newsId);

    if (result.success) {
      setMessage({ type: 'success', text: 'Article deleted successfully' });
      
      if (user) {
        const deletedNews = newsList.find(n => n.id === newsId);
        await logsService.createLog(
          user.id,
          user.email,
          'NEWS_DELETE',
          {
            news_id: newsId,
            title: deletedNews?.title
          }
        );
      }
      
      loadNews();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  return (
    <div className="news-manager">
      <div className="news-tabs">
        <button
          className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          All Articles ({newsList.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('create');
            setEditingId(null);
            setFormData({ title: '', content: '', authorName: '', scheduledDate: '', headerImage: null, headerImageUrl: '' });
            setBlocks([]);
          }}
        >
          ➕ Create Article
        </button>

      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="news-create-wrapper">
          <div className="news-form">
            <div className="form-group">
              <label>Article Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter article title"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label>Author Name</label>
              <input
                type="text"
                name="authorName"
                placeholder="Enter your name or author name"
                value={formData.authorName || getAuthorName()}
                onChange={handleInputChange}
                disabled={loading}
              />
              <small>Will default to your account name if left blank</small>
            </div>

            <div className="form-group">
              <label>Schedule Publication (Optional)</label>
              <input
                type="datetime-local"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleInputChange}
                disabled={loading}
              />
              <small>Leave empty to save as draft</small>
            </div>

            <div className="form-group">
              <label>Header Image (Optional)</label>
              <div className="header-image-preview">
                {formData.headerImageUrl ? (
                  <img src={formData.headerImageUrl} alt="Header preview" />
                ) : (
                  <div className="no-image">No image</div>
                )}
              </div>
              <input
                type="file"
                name="headerImage"
                onChange={handleInputChange}
                disabled={loading || uploadingImage}
                accept="image/*"
                className="image-input"
              />
              <small>Recommended size: 1200x400px {uploadingImage && '(uploading...)'}</small>
            </div>

            <div className="form-actions">
              <button 
                onClick={() => editingId ? handleUpdateNews(null, editingId, false) : handleCreateNews(null)}
                disabled={loading || blocks.length === 0} 
                className="submit-button"
              >
                {loading ? 'Saving...' : (editingId ? 'Submit for Review' : 'Submit for Review')}
              </button>
              <button 
                onClick={() => editingId ? handleUpdateNews(null, editingId, true) : handleCreateNews(null, true)}
                disabled={loading || blocks.length === 0} 
                className="draft-button"
              >
                {loading ? 'Saving...' : '💾 Save as Draft'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({ title: '', content: '', authorName: '', scheduledDate: '', headerImage: null, headerImageUrl: '' });
                    setBlocks([]);
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <ArticleBuilder 
            initialBlocks={blocks}
            onChange={setBlocks}
            onSave={() => editingId ? handleUpdateNews(null, editingId) : handleCreateNews(null)}
            isSaving={loading}
            title={formData.title}
            authorName={formData.authorName}
            headerImage={formData.headerImage}
          />
        </div>
      )}

      {activeTab === 'list' && (
        <div className="news-list-container">
          <div className="list-controls">
            <button
              className={`filter-button ${viewMode === 'my' ? 'active' : ''}`}
              onClick={() => setViewMode('my')}
            >
              My Articles ({userArticles.length})
            </button>
            {hasAnyPermission(['publish_articles', 'edit_news']) && (
              <button
                className={`filter-button ${viewMode === 'review' ? 'active' : ''}`}
                onClick={() => setViewMode('review')}
              >
                📋 Review Queue ({viewMode === 'review' ? newsList.length : newsList.filter(n => n.status === 'pending_review').length})
              </button>
            )}
            {hasPermission('manage_permissions') && (
              <button
                className={`filter-button ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}
              >
                All Articles ({newsList.length})
              </button>
            )}
          </div>
          
          <div className="news-list">
            {newsList.length === 0 ? (
              <p className="no-articles">No articles yet</p>
            ) : (
              newsList.map(news => (
                <div key={news.id} className={`news-item status-${news.status}`}>
                  <div className="news-header">
                    <h3>{news.title}</h3>
                    <span className={`status-badge ${news.status}`}>{news.status}</span>
                  </div>

                  <p className="news-excerpt">{news.content.substring(0, 150)}...</p>

                  <div className="news-meta">
                    <small>
                      Created by {news.created_by_name} • {formatDateEST(news.created_at)}
                      {news.last_edited_by_name && news.last_edited_by_name !== news.created_by_name && (
                        <> • Edited by {news.last_edited_by_name} on {formatDateEST(news.updated_at)}</>
                      )}
                    </small>
                  </div>

                <div className="news-actions">
                  <button
                    onClick={() => handleEditClick(news)}
                    disabled={loading}
                    className="edit-button"
                  >
                    Edit
                  </button>

                  {!news.is_published && hasAnyPermission(['publish_articles', 'edit_news']) && (
                    <button
                      onClick={() => setPreviewArticle(news)}
                      disabled={loading}
                      className="preview-button"
                    >
                      👁️ Preview
                    </button>
                  )}

                  {hasAnyPermission(['publish_articles']) && (
                    <button
                      onClick={() => handlePublish(news.id, news.is_published)}
                      disabled={loading}
                      className={`publish-button ${news.is_published ? 'unpublish' : 'publish'}`}
                    >
                      {news.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                  )}

                  <button
                    onClick={() => handleDelete(news.id)}
                    disabled={loading}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        </div>
      )}

      {previewArticle && (
        <div className="preview-modal-overlay" onClick={() => setPreviewArticle(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="preview-close"
              onClick={() => setPreviewArticle(null)}
            >
              ✕
            </button>
            
            <div className="preview-content">
              <h1 className="preview-title">{previewArticle.title}</h1>
              
              {previewArticle.header_image && (
                <img 
                  src={previewArticle.header_image} 
                  alt={previewArticle.title}
                  className="preview-header-image"
                />
              )}
              
              <div className="preview-metadata">
                <span className="written-by">✍️ Written By: <strong>{previewArticle.created_by_name}</strong></span>
                <span className="published-date">{formatDateEST(previewArticle.created_at)}</span>
              </div>

              <div className="preview-body">
                {(() => {
                  const blocks = newsService.parseContent(previewArticle);
                  if (!blocks || blocks.length === 0) {
                    return <p>No content available</p>;
                  }
                  return blocks.map((block, idx) => (
                    <div key={idx} className={`preview-block preview-block-${block.type}`}>
                      {block.type === 'text' && <p>{block.content}</p>}
                      {block.type === 'image' && <img src={block.content} alt="content" />}
                      {block.type === 'heading' && <h2>{block.content}</h2>}
                      {block.type === 'quote' && <blockquote>{block.content}</blockquote>}
                    </div>
                  ));
                })()}
              </div>

              <div className="preview-footer">
                <small>Preview of how this article will appear on the News page</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {publishModalOpen && (
        <div className="publish-modal-overlay" onClick={() => setPublishModalOpen(false)}>
          <div className="publish-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Publish Article</h2>
            
            <div className="publish-form-group">
              <label htmlFor="publisher-name">Publisher Name</label>
              <input
                id="publisher-name"
                type="text"
                value={publisherNameInput}
                onChange={(e) => setPublisherNameInput(e.target.value)}
                placeholder="Enter publisher name"
                disabled={loading}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleConfirmPublish()}
              />
            </div>

            {mediaWebhooks.length > 0 ? (
              <div className="publish-form-group">
                <label>Publish to Channels</label>
                <p className="help-text">Select which channels to publish this article to</p>
                <div className="channels-list">
                  {mediaWebhooks.map(webhook => (
                    <label key={webhook.id} className="channel-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedChannels.includes(webhook.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedChannels([...selectedChannels, webhook.id]);
                          } else {
                            setSelectedChannels(selectedChannels.filter(id => id !== webhook.id));
                          }
                        }}
                        disabled={loading}
                      />
                      <span>{webhook.channel || webhook.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="publish-form-group">
                <p className="help-text" style={{ color: '#ff9800', marginBottom: '1rem' }}>
                  ⚠️ No media webhooks configured. Configure webhooks in the Webhooks Manager and mark them as "Media/Article Webhook" to publish articles.
                </p>
              </div>
            )}

            <div className="publish-modal-actions">
              <button 
                onClick={handleConfirmPublish}
                disabled={loading || !publisherNameInput.trim()}
                className="confirm-publish-btn"
              >
                {loading ? 'Publishing...' : 'Publish Article'}
              </button>
              <button 
                onClick={() => {
                  setPublishModalOpen(false);
                  setSelectedChannels([]);
                }}
                disabled={loading}
                className="cancel-publish-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManager;
